import { useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Loader2, Save, Upload, X, Image as ImageIcon } from 'lucide-react';
import { api, uploadFile } from '@/api/client';
import { useAuth } from '@/hooks/useAuth';
import { SignedImage } from '@/components/ui/SignedImage';
import { MerchantStatusToggle } from '@/components/merchant/MerchantStatusToggle';
import { OwnerSelect } from '@/components/merchant/OwnerSelect';
import { MobileOtpVerify } from '@/components/merchant/MobileOtpVerify';

interface Merchant {
  id: number;
  companyName: string;
  brandName?: string;
  email: string;
  mobile?: string;
  status: string;
  serviceType?: string;
  billingMode?: string;
  postpaidLimit?: number;
  enforceWalletBalance?: boolean;
  remittanceTatDays?: number;
  logoUrl?: string;
  isAutoShip?: boolean;
  isIvrEnabled?: boolean;
  salesUserId?: number | null;
  csUserId?: number | null;
  salesUserName?: string | null;
  csUserName?: string | null;
  secondaryEmails?: string | null;
  createdAt: string;
}

const SERVICE_TYPE_LABEL: Record<string, string> = {
  BOTH: 'Both (B2C + B2B)',
  B2C: 'B2C only',
  B2B: 'B2B only',
};

interface FormData {
  companyName: string;
  brandName?: string;
  email?: string;
  mobile?: string;
  serviceType?: 'B2C' | 'B2B' | 'BOTH';
  billingMode?: 'PREPAID' | 'POSTPAID';
  postpaidLimit?: number;
  enforceWalletBalance?: boolean;
  remittanceTatDays?: number;
  logoUrl?: string;
  isAutoShip?: boolean;
  isIvrEnabled?: boolean;
  salesUserId?: number | null;
  csUserId?: number | null;
  secondaryEmails?: string;
}

/**
 * `merchantView` = the merchant is editing their OWN profile from Settings, not an admin editing theirs.
 * Two things change: the save goes to `/merchants/me/profile` (PATCH `/merchants/:id` is SUPER_ADMIN/ADMIN
 * only, so a merchant saving here used to get a 403), and the commercial / internal fields the backend's
 * `updateOwnProfile` deliberately refuses to write are not shown as editable — a merchant only owns
 * company name, brand name, mobile and logo.
 */
export function MerchantProfileTab({ merchant, merchantView }: { merchant: Merchant; merchantView?: boolean }) {
  const qc = useQueryClient();
  const { user } = useAuth();
  // Email is the merchant's LOGIN id, so only SUPER_ADMIN / ADMIN may change it — the same roles the
  // backend PATCH /merchants/:id route allows. Everyone else keeps the old read-only field.
  const canEditEmail = !merchantView && (user?.userType === 'SUPER_ADMIN' || user?.userType === 'ADMIN');
  const { register, handleSubmit, reset, setValue, watch, formState: { errors, isDirty } } = useForm<FormData>({
    defaultValues: {
      companyName: merchant.companyName,
      brandName: merchant.brandName,
      email: merchant.email,
      mobile: merchant.mobile,
      serviceType: (merchant.serviceType as any) ?? 'BOTH',
      billingMode: (merchant.billingMode as any) ?? 'PREPAID',
      postpaidLimit: merchant.postpaidLimit ?? 0,
      enforceWalletBalance: merchant.enforceWalletBalance ?? false,
      remittanceTatDays: merchant.remittanceTatDays ?? 5,
      logoUrl: merchant.logoUrl,
      isAutoShip: merchant.isAutoShip ?? false,
      isIvrEnabled: merchant.isIvrEnabled ?? false,
      salesUserId: merchant.salesUserId ?? null,
      csUserId: merchant.csUserId ?? null,
      secondaryEmails: merchant.secondaryEmails ?? '',
    },
  });

  const watchedLogo = watch('logoUrl');
  const watchedMobile = watch('mobile') ?? '';
  // A changed mobile has to prove it can receive an SMS before it is saved. Comparing the digits
  // means reformatting the same number (spaces, +91) is not treated as a change.
  const digits = (v: any) => String(v ?? '').replace(/\D+/g, '').slice(-10);
  const mobileChanged = digits(watchedMobile) !== digits(merchant.mobile);
  const [otpId, setOtpId] = useState<string | null>(null);
  const watchedEnforce = watch('enforceWalletBalance');
  const watchedSales = watch('salesUserId');
  const watchedCs = watch('csUserId');

  const save = useMutation({
    mutationFn: async (d: FormData) => {
      const payload: any = { ...d, ...(mobileChanged ? { otpId } : {}) };
      // Emptying the textarea MUST reach the API as an explicit null — the blanket empty-string strip
      // below would drop the key instead, and clearing the list would silently do nothing.
      const clearedEmails = payload.secondaryEmails !== undefined && String(payload.secondaryEmails).trim() === '';
      // strip empty strings so optional fields aren't validated. `null` is kept deliberately —
      // it's how an owner assignment is cleared.
      Object.keys(payload).forEach(k => { if (payload[k] === '') delete payload[k]; });
      if (clearedEmails) payload.secondaryEmails = null;
      // Never send an email the user isn't allowed to change (the backend would 403 the whole save).
      if (!canEditEmail) delete payload.email;
      if (merchantView) {
        // Not rendered on the merchant portal, so they'd only ever be form defaults — and the backend
        // ignores them anyway. Dropping them keeps the request honest about what is being changed.
        ['serviceType', 'billingMode', 'postpaidLimit', 'enforceWalletBalance', 'remittanceTatDays',
          'salesUserId', 'csUserId', 'isAutoShip', 'isIvrEnabled', 'secondaryEmails'].forEach(k => delete payload[k]);
        return (await api.patch('/merchants/me/profile', payload)).data;
      }
      return (await api.patch(`/merchants/${merchant.id}`, payload)).data;
    },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['merchant', merchant.id] });
      qc.invalidateQueries({ queryKey: ['merchants'] });
      // The merchant's own profile feeds the sidebar logo and the label-settings panel too.
      qc.invalidateQueries({ queryKey: ['merchant', 'me', 'profile'] });
      qc.invalidateQueries({ queryKey: ['merchant-label-prefs'] });
      toast.success('Profile updated');
      reset({
        companyName: data.companyName,
        brandName: data.brandName,
        email: data.email,
        mobile: data.mobile,
        serviceType: data.serviceType,
        billingMode: data.billingMode,
        postpaidLimit: Number(data.postpaidLimit) || 0,
        enforceWalletBalance: data.enforceWalletBalance ?? false,
        remittanceTatDays: data.remittanceTatDays,
        logoUrl: data.logoUrl,
        isAutoShip: data.isAutoShip,
        isIvrEnabled: data.isIvrEnabled,
        salesUserId: data.salesUserId ?? null,
        csUserId: data.csUserId ?? null,
        secondaryEmails: data.secondaryEmails ?? '',
      });
    },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Failed to save profile'),
  });

  return (
    <form onSubmit={handleSubmit((d) => save.mutate(d))} className="space-y-5">
      <div className="grid grid-cols-2 gap-4">
        <Field label="Company Name *" error={errors.companyName?.message}>
          <input {...register('companyName', { required: 'Required', minLength: { value: 2, message: 'Min 2 chars' } })} className="input" />
        </Field>
        <Field label="Brand Name">
          <input {...register('brandName')} className="input" />
        </Field>

        {canEditEmail ? (
          <Field label="Email (login id)" error={errors.email?.message}>
            <input type="email" {...register('email', {
              required: 'Required',
              pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Enter a valid email' },
            })} className="input" />
            <p className="text-xs text-amber-600 mt-1">Changing this changes the email the merchant signs in with.</p>
          </Field>
        ) : (
          <Field label="Email (read-only)">
            <input value={merchant.email} disabled className="input bg-slate-50 text-slate-500" />
          </Field>
        )}
        <Field label="Mobile" error={errors.mobile?.message}>
          <input {...register('mobile', {
            pattern: { value: /^[6-9]\d{9}$/, message: '10 digits starting 6-9' },
          })} placeholder="10-digit Indian mobile" className="input" />
          {/* Only shown once the number has actually been edited — an unchanged number needs nothing. */}
          {mobileChanged && (
            <MobileOtpVerify mobile={watchedMobile} purpose="MERCHANT_MOBILE_CHANGE" otpId={otpId} onVerified={setOtpId} disabled={save.isPending} />
          )}
        </Field>

        {/* Commercial terms and account ownership are set by ShipRoute, not by the merchant — on the merchant
            portal they are shown read-only (the backend's updateOwnProfile refuses to write them anyway). */}
        {merchantView ? (
          <>
            <Field label="Service Type (read-only)">
              <input value={SERVICE_TYPE_LABEL[merchant.serviceType ?? 'BOTH'] ?? merchant.serviceType ?? '—'}
                disabled className="input bg-slate-50 text-slate-500" />
            </Field>
            <Field label="Billing Mode (read-only)">
              <input value={merchant.billingMode === 'POSTPAID' ? 'Postpaid' : 'Prepaid'}
                disabled className="input bg-slate-50 text-slate-500" />
            </Field>
          </>
        ) : (
          <>
            <Field label="Service Type">
              <select {...register('serviceType')} className="input">
                <option value="BOTH">Both (B2C + B2B)</option>
                <option value="B2C">B2C only</option>
                <option value="B2B">B2B only</option>
              </select>
            </Field>
            <Field label="Billing Mode">
              <select {...register('billingMode')} className="input">
                <option value="PREPAID">Prepaid</option>
                <option value="POSTPAID">Postpaid</option>
              </select>
            </Field>

            <Field label="Postpaid Limit (₹)">
              <input type="number" step="1" {...register('postpaidLimit', { valueAsNumber: true, min: 0 })} className="input" />
            </Field>
            <Field label="Remittance TAT (days)">
              <input type="number" step="1" {...register('remittanceTatDays', { valueAsNumber: true, min: 1 })} className="input" />
            </Field>

            {/* Switching this on can stop a merchant shipping, so their live position is shown right
                here rather than one tab away — the number is the whole basis of the decision. */}
            <div className="md:col-span-2">
              <WalletGateField
                merchantId={merchant.id}
                enabled={!!watchedEnforce}
                onChange={(v) => setValue('enforceWalletBalance', v, { shouldDirty: true })}
              />
            </div>

            {/* Account ownership — used to slice sales / support reporting */}
            <Field label="Sales Person">
              <OwnerSelect value={watchedSales ?? null} preferType="SALES_PERSON" placeholder="Not assigned"
                onChange={(v) => setValue('salesUserId', v, { shouldDirty: true })} />
            </Field>
            <Field label="CS Person (Customer Support)">
              <OwnerSelect value={watchedCs ?? null} preferType="CUSTOMER_SUPPORT" placeholder="Not assigned"
                onChange={(v) => setValue('csUserId', v, { shouldDirty: true })} />
            </Field>

            {/* The client's OTHER contact addresses. Kept separate from the login email above (changing
                that changes what they sign in with) and from Notifications, where each list drives a
                specific automated send. Nothing is mailed from here — it is an address book. */}
            <div className="col-span-2">
              <Field label="Other Emails" error={errors.secondaryEmails?.message}>
                <textarea
                  {...register('secondaryEmails', {
                    // Refuse a malformed address rather than accepting it and failing silently at send
                    // time. Separators are free — one per line, comma or semicolon all read the same.
                    validate: (v) => {
                      const parts = String(v ?? '').split(/[\n,;]+/).map(s => s.trim()).filter(Boolean);
                      const bad = parts.filter(p => !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(p));
                      return bad.length === 0 || `Not a valid email: ${bad.slice(0, 3).join(', ')}`;
                    },
                  })}
                  rows={3}
                  placeholder={'One per line, or separated by commas / semicolons\naccounts@client.com\nlogistics@client.com'}
                  className="input font-mono text-xs leading-relaxed"
                />
                <p className="text-xs text-slate-500 mt-1">
                  Extra contacts at this client. Not the login, and nothing is emailed from here —
                  automated sends are set under <span className="font-medium">Notifications</span>.
                </p>
              </Field>
            </div>
          </>
        )}

        <LogoUploader
          currentUrl={watchedLogo}
          onChange={(url) => setValue('logoUrl', url, { shouldDirty: true })}
        />
        <Field label="Status">
          {merchantView
            ? <input value={merchant.status ?? '—'} disabled className="input bg-slate-50 text-slate-500" />
            : <MerchantStatusToggle merchantId={merchant.id} status={merchant.status} variant="inline" />}
        </Field>
      </div>

      <div className="flex items-center gap-6 border-t border-slate-100 pt-4">
        {/* Both switches are ShipRoute-side account settings. The merchant portal shows auto-ship read-only so
            they can see how their orders behave, and does not show IVR at all. */}
        <label className="flex items-center gap-2 text-sm text-slate-700">
          <input type="checkbox" {...register('isAutoShip')} disabled={merchantView} className="rounded disabled:opacity-60" />
          Auto-ship enabled
        </label>
        {!merchantView && (
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input type="checkbox" {...register('isIvrEnabled')} className="rounded" />
            IVR enabled (NDR voice callbacks)
          </label>
        )}
        <div className="flex-1" />
        <button type="button" onClick={() => reset()} disabled={!isDirty}
          className="px-3 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg disabled:opacity-50">Reset</button>
        <button type="submit" disabled={save.isPending || !isDirty || (mobileChanged && !otpId)}
          title={mobileChanged && !otpId ? 'Verify the new mobile number by OTP first' : undefined}
          className="px-4 py-2 bg-primary text-white rounded-lg font-medium hover:bg-primary-700 transition disabled:opacity-60 flex items-center gap-2 text-sm">
          {save.isPending ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
          Save Profile
        </button>
      </div>
    </form>
  );
}

function Field({ label, children, error }: { label: string; children: React.ReactNode; error?: string }) {
  return (
    <div>
      <label className="block text-xs font-medium text-slate-600 mb-1">{label}</label>
      {children}
      {error && <p className="text-danger text-xs mt-1">{error}</p>}
    </div>
  );
}

/**
 * The wallet gate, with the merchant's live position beside it.
 *
 * The switch is shown next to the number it acts on deliberately: turning it on for a merchant whose
 * wallet has never been credited stops them shipping the moment they try, and the only way to know
 * that in advance is to read the balance. `available` is derived (recharge + COD we hold − freight
 * billed), not a stored column, so it is fetched rather than taken from the merchant row.
 */
function WalletGateField({ merchantId, enabled, onChange }: { merchantId: number; enabled: boolean; onChange: (v: boolean) => void }) {
  const { data, isLoading } = useQuery<any>({
    queryKey: ['shipping-balance', merchantId],
    queryFn: async () => (await api.get('/wallet/shipping-balance', { params: { merchantId } })).data,
  });
  const inr = (v: number) => '₹' + Number(v ?? 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const available = Number(data?.available ?? 0);
  const past = data ? available < Number(data.floor ?? 0) : false;

  return (
    <div className={`rounded-lg border p-3 ${enabled && past ? 'border-rose-200 bg-rose-50' : 'border-slate-200 bg-slate-50'}`}>
      <label className="flex items-start gap-2 text-sm text-slate-700 cursor-pointer">
        <input type="checkbox" checked={enabled} onChange={(e) => onChange(e.target.checked)} className="rounded mt-0.5" />
        <span>
          <span className="font-medium">Enforce wallet balance on shipping</span>
          <span className="block text-xs text-slate-500 mt-0.5">
            Refuses <strong>Ship</strong> once the balance goes past its floor, with a recharge message showing
            the amount. Off means this merchant ships exactly as today. Creating, uploading, printing
            and tracking orders are never affected — only handing a shipment to a courier.
          </span>
        </span>
      </label>
      <div className="mt-2 pl-6 text-xs">
        {isLoading ? (
          <span className="text-slate-400">Checking balance…</span>
        ) : data ? (
          <>
            <span className={past ? 'font-semibold text-rose-600' : 'font-semibold text-emerald-700'}>{inr(available)}</span>
            <span className="text-slate-500">
              {' '}available — {inr(data.recharge)} recharge + {inr(data.remittanceHeld)} COD held − {inr(data.freight)} freight billed
            </span>
            {past && (
              <p className="text-rose-600 mt-1">
                {enabled
                  ? `Shipping is blocked — this merchant must add ${inr(data.shortfall)}.`
                  : `Already past the floor. Switching this on will stop them shipping until they add ${inr(Number(data.floor ?? 0) - available)}.`}
              </p>
            )}
          </>
        ) : (
          <span className="text-slate-400">Balance unavailable</span>
        )}
      </div>
    </div>
  );
}

const LOGO_MAX_BYTES = 500 * 1024; // 500 KB
const LOGO_MAX_DIM = 800;          // px (width OR height)
const LOGO_TYPES = ['image/png', 'image/jpeg', 'image/jpg'];

function LogoUploader({ currentUrl, onChange }: { currentUrl?: string; onChange: (url: string) => void }) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleFile = async (file: File) => {
    setError(null);
    if (!LOGO_TYPES.includes(file.type)) { setError('Only PNG and JPG/JPEG are allowed.'); return; }
    if (file.size > LOGO_MAX_BYTES) { setError(`File too large (${(file.size / 1024).toFixed(0)} KB). Max ${(LOGO_MAX_BYTES / 1024).toFixed(0)} KB.`); return; }
    setBusy(true);
    try {
      const dataUrl = await new Promise<string>((resolve, reject) => {
        const r = new FileReader();
        r.onload = () => resolve(String(r.result));
        r.onerror = reject;
        r.readAsDataURL(file);
      });
      // dimension check
      await new Promise<void>((resolve, reject) => {
        const img = new Image();
        img.onload = () => {
          if (img.width > LOGO_MAX_DIM || img.height > LOGO_MAX_DIM) {
            reject(new Error(`Image too large: ${img.width}\u00d7${img.height} px. Max ${LOGO_MAX_DIM}\u00d7${LOGO_MAX_DIM}.`));
          } else { resolve(); }
        };
        img.onerror = () => reject(new Error('Invalid image file.'));
        img.src = dataUrl;
      });
      // Store the file in R2 (folder merchant_logo) and persist its ref (not the base64 blob).
      const { ref } = await uploadFile(file, 'merchant_logo');
      onChange(ref);
    } catch (e: any) {
      setError(e?.message ?? 'Upload failed');
    } finally { setBusy(false); }
  };

  return (
    <div className="col-span-2">
      <label className="block text-xs font-medium text-slate-600 mb-1">
        Logo <span className="text-slate-400 font-normal">· PNG/JPG, max 500 KB, max 800×800 px</span>
      </label>
      <div className="flex items-start gap-3">
        <div className="w-24 h-24 rounded-lg border-2 border-dashed border-slate-200 bg-slate-50 flex items-center justify-center overflow-hidden flex-shrink-0">
          {currentUrl ? (
            <SignedImage src={currentUrl} alt="Merchant logo" className="w-full h-full object-contain"
              fallback={<ImageIcon size={24} className="text-slate-300" />} />
          ) : (
            <ImageIcon size={24} className="text-slate-300" />
          )}
        </div>
        <div className="flex-1 space-y-2">
          <div className="flex items-center gap-2">
            <input
              ref={inputRef}
              type="file"
              accept="image/png,image/jpeg,image/jpg"
              className="hidden"
              onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFile(f); }}
            />
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              disabled={busy}
              className="px-3 py-1.5 border border-slate-300 text-slate-700 text-xs rounded-lg hover:bg-slate-50 disabled:opacity-60 flex items-center gap-1">
              {busy ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />}
              {currentUrl ? 'Replace Logo' : 'Upload Logo'}
            </button>
            {currentUrl && (
              <button
                type="button"
                onClick={() => onChange('')}
                className="px-3 py-1.5 border border-slate-300 text-slate-500 text-xs rounded-lg hover:bg-red-50 hover:text-danger hover:border-red-200 flex items-center gap-1">
                <X size={12} /> Remove
              </button>
            )}
          </div>
          {error && <p className="text-danger text-xs">{error}</p>}
          <p className="text-xs text-slate-400">
            Used on the merchant login screen, sidebar after login, and printed on shipping address labels.
          </p>
        </div>
      </div>
    </div>
  );
}
